import { useState } from 'react';
import { ShieldCheck, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui';
import { CreateUserFromCaseModal } from './CreateUserFromCaseModal';
import type { OnboardingCase } from '../types/onboarding';

/**
 * "Create user account" CTA on the case detail page. Two eligibility
 * paths, per User-Onboarding.md §4.1:
 *
 *   - `onboarding_complete` — onboarding finished cleanly, account
 *     provisioning is the natural next step.
 *   - `bgv_verification_phase` — background verification is in flight
 *     but can take days. The CTA stays available so the user can be
 *     provisioned in parallel and start working while BGV completes
 *     asynchronously. We surface that intent explicitly so the
 *     reviewer doesn't think they're jumping a step.
 *
 * Both states + this whole flow (`POST /console/user_onboarding/create/
 * user/:case_id` under the hood) are **ONB-only**. The new
 * multi-workflow doc calls this out explicitly: the create-user
 * route stays onboarding-scoped even though the surrounding case
 * list / edit surface now covers CR and other registered workflows.
 * The `workflowAbv` prop below is what enforces that guard client-
 * side — see the eligibility check.
 *
 * Modal flow is identical in both states — it's just the banner copy
 * and chrome that differ to set expectations.
 */

const ELIGIBLE_STATES = new Set(['onboarding_complete', 'bgv_verification_phase']);
const BGV_STATE = 'bgv_verification_phase';
const ONB_WORKFLOW_ABV = 'ONB';

interface CreateUserBannerProps {
  candidate: OnboardingCase;
  /** Source of truth for current state — workflow GET when available. */
  liveState?: string;
  /**
   * Workflow the case belongs to. Resolved by the parent from the
   * workflows registry keyed on `candidate_case.workflow_id`. When
   * this is undefined (registry unreachable, `workflow_id` missing,
   * lookup miss) we assume ONB — matches the server's own default
   * and preserves the pre-multi-workflow behaviour so a registry
   * outage doesn't accidentally suppress a valid provisioning CTA.
   * When the caller resolves it and it's *not* ONB (e.g. CR), the
   * banner stays hidden regardless of state.
   */
  workflowAbv?: string;
}

interface BannerCopy {
  title: string;
  description: string;
  badge?: { label: string; icon: React.ReactNode };
}

const copyFor = (state: string): BannerCopy => {
  if (state === BGV_STATE) {
    return {
      title: 'Background verification in progress — create user account in parallel',
      description:
        'BGV can take time. You can provision the user now so they can start working while verification completes asynchronously.',
      badge: {
        label: 'BGV in progress',
        icon: <ShieldCheck className="h-3.5 w-3.5" />,
      },
    };
  }
  return {
    title: 'Ready to create user account',
    description:
      "The candidate is eligible for user provisioning. Pick roles and verify the address — the server will assemble the rest from the case.",
  };
};

export const CreateUserBanner = ({ candidate, liveState, workflowAbv }: CreateUserBannerProps) => {
  const [open, setOpen] = useState(false);
  const state = liveState ?? candidate.state;
  // Workflow guard first, then state. The state literals
  // (`onboarding_complete`, `bgv_verification_phase`) are ONB names
  // — if a CR (or any other) workflow happens to have registered a
  // state with the same title, showing this banner would kick off a
  // create-MIS-user call that the ONB-only endpoint will reject
  // with a confusing 400. Better to hide the CTA up front.
  const workflowKnownNonOnb =
    typeof workflowAbv === 'string' &&
    workflowAbv !== '' &&
    workflowAbv.toUpperCase() !== ONB_WORKFLOW_ABV;
  if (workflowKnownNonOnb) return null;
  if (!ELIGIBLE_STATES.has(state)) return null;

  const copy = copyFor(state);

  return (
    <>
      <section className="rounded-lg border border-rgf-primary-200 bg-rgf-primary-50 px-4 py-3 sm:px-5 sm:py-4 flex items-start sm:items-center gap-3 sm:gap-4 flex-col sm:flex-row">
        <div className="flex items-center gap-3 flex-1">
          <span className="flex-shrink-0 rounded-full bg-rgf-primary-100 p-2 text-rgf-primary-700">
            <UserPlus className="h-5 w-5" />
          </span>
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <p className="text-sm font-semibold text-rgf-gray-900">{copy.title}</p>
              {copy.badge && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                  {copy.badge.icon}
                  {copy.badge.label}
                </span>
              )}
            </div>
            <p className="text-xs text-rgf-gray-600">{copy.description}</p>
          </div>
        </div>
        <Button
          type="button"
          icon={<UserPlus className="h-4 w-4" />}
          onClick={() => setOpen(true)}
        >
          Create User Account
        </Button>
      </section>

      <CreateUserFromCaseModal
        caseId={candidate.id}
        isOpen={open}
        onClose={() => setOpen(false)}
        onSuccess={() => setOpen(false)}
      />
    </>
  );
};
